import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { DiagnosticRating } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { PartnerThemeContext } from ':core/context/partner-theme.context';
import { RatingBlocComponent } from ':modules/rating-bloc/rating-bloc.component';
import { RatingChipComponent } from ':modules/rating-chip/rating-chip.component';
import { Icon, MaloupePlatformKey } from ':shared/enums';
import {
    Illustration,
    IllustrationPathResolverPipe,
    ImagePathResolverPipe,
    LogoPathResolverPipe,
    PluralTranslatePipe,
} from ':shared/pipes';

@Component({
    selector: 'app-diagnostic-inconsistencies',
    imports: [
        NgClass,
        TranslateModule,
        RatingBlocComponent,
        RatingChipComponent,
        IllustrationPathResolverPipe,
        ImagePathResolverPipe,
        LogoPathResolverPipe,
        PluralTranslatePipe,
    ],
    templateUrl: './diagnostic-inconsistencies.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticInconsistenciesComponent {
    readonly diagnostic = input.required<Diagnostic>();

    private readonly _partnerThemeContext = inject(PartnerThemeContext);

    readonly DiagnosticRating = DiagnosticRating;
    readonly Illustration = Illustration;
    readonly Icon = Icon;

    readonly partnerTheme = this._partnerThemeContext.activeTheme;

    readonly inconsistencies = computed(() => this.diagnostic().inconsistencies);

    readonly inconsistencesRating = computed(() => this.diagnostic().getInconsistencyRating());

    readonly photosRating = computed(() => this.diagnostic().getPhotosRating());
    readonly serviceCount = computed(() => this.diagnostic().getServiceCount());
    readonly presentServiceCount = computed(() => this.diagnostic().getPresentServiceCount());
    readonly servicesRating = computed(() => this.diagnostic().getServicesRating());

    readonly platformKeysToDisplay = computed(() => {
        const inconsistencies = this.inconsistencies();
        if (!inconsistencies?.platformKeys) {
            return [];
        }
        const platformsToDisplay: string[] = [
            MaloupePlatformKey.FACEBOOK,
            MaloupePlatformKey.TRIPADVISOR,
            MaloupePlatformKey.FOURSQUARE,
            MaloupePlatformKey.BING,
        ];
        const platformKeys = inconsistencies.platformKeys.map((key) => key.toLowerCase());
        const foundPlatforms = platformsToDisplay.filter((key) => platformKeys.includes(key));
        return [MaloupePlatformKey.GMB, MaloupePlatformKey.APPLE, ...foundPlatforms];
    });

    readonly otherPlatformCount = computed(() => {
        const inconsistencies = this.inconsistencies();
        const displayedPlatforms = this.platformKeysToDisplay();
        if (!inconsistencies || inconsistencies.platformCount < displayedPlatforms.length) {
            return 0;
        }
        return inconsistencies.platformCount - displayedPlatforms.length;
    });
}
